import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import HeadersNonAuth from '../../components/base/HeadersNonAuth';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, error } = useAuthContext();
  const { notify } = useNotifications();
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  const [formData, setFormData] = useState({
    emailOrUsername: '',
    password: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        emailOrUsername: rememberedEmail
      }));
      setRememberMe(true);
      console.log('📧 Loaded remembered email:', rememberedEmail);
    }
  }, []);

  // Clear loading state when user is redirected away from login page
  useEffect(() => {
    const handleBeforeUnload = () => {
      setIsLoading(false);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

    const validateForm = async () => {
    const errors = {};
    setIsChecking(true);
    
    // Email or Username validation
    if (!formData.emailOrUsername.trim()) {
      errors.emailOrUsername = 'Email or username is required';
    } else {
      // Check if it's an email format
      const isEmail = formData.emailOrUsername.includes('@');
      
      if (isEmail) {
        // Validate email format
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailOrUsername)) {
          errors.emailOrUsername = 'Please enter a valid email';
        }
        // Let Firebase Auth handle authentication
      } else {
        // It's a username, validate username format only
        if (formData.emailOrUsername.length < 3) {
          errors.emailOrUsername = 'Username must be at least 3 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.emailOrUsername)) {
          errors.emailOrUsername = 'Username can only contain letters, numbers, and underscores';
        }
        // Let AuthContext handle username lookup in Firestore
      }
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    setIsChecking(false);
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setIsLoading(true);

    try {
      // Execute reCAPTCHA v3 — invisible, score-based
      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha('login');
        const controller = new AbortController();
        const captchaTimeout = setTimeout(() => controller.abort(), 8000);
        try {
          const verifyRes = await fetch(
            `${import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net'}/verify_recaptcha`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: recaptchaToken, action: 'login' }),
              signal: controller.signal,
            }
          );
          const verifyData = await verifyRes.json();
          if (!verifyData.success) {
            setIsLoading(false);
            setValidationErrors({ form: 'Security check failed. Please try again.' });
            return;
          }
        } catch (captchaErr) {
          // On timeout or network error — fail open so legitimate users are not blocked
          console.warn('reCAPTCHA verification skipped:', captchaErr.message);
        } finally {
          clearTimeout(captchaTimeout);
        }
      }

      console.log('🔐 Login attempt starting...');
      console.log('Remember me:', rememberMe);
      
      // Handle remember me functionality
      if (rememberMe) {
        // Store login credentials in localStorage (for demo purposes)
        // In production, you might want to use more secure methods
        localStorage.setItem('rememberedEmail', formData.emailOrUsername);
        console.log('✅ Remember me enabled - credentials stored');
      } else {
        // Clear stored credentials if remember me is unchecked
        localStorage.removeItem('rememberedEmail');
        console.log('❌ Remember me disabled - credentials cleared');
      }
      
      console.log('🚀 Starting signIn process...');
      
      // Start signIn process
      await signIn(formData.emailOrUsername, formData.password, async () => {
        console.log('✅ Login successful, sending notification...');
        try {
          await notify.loginSuccess();
          console.log('✅ Login notification sent successfully');
        } catch (notifError) {
          console.error('❌ Failed to send login notification:', notifError);
        }
        
        // Check if there's a redirect URL saved (e.g., from OAuth callback)
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          console.log('🔄 Redirecting to saved URL:', redirectUrl);
          sessionStorage.removeItem('redirectAfterLogin');
          window.location.href = redirectUrl;
        } else {
          // Decide destination based on broker connections
          try {
            const q = query(collection(db, 'tbl_broker_integration'), where('userId', '==', (await import('../../services/firebase')).auth.currentUser.uid));
            const snapshot = await getDocs(q);
            const hasConnections = snapshot.docs.some(d => {
              const data = d.data();
              return data && (data.status === 'active' || data.status === 'connected');
            });
            console.log('🔎 Post-login connection check:', { count: snapshot.size, hasConnections });
            if (hasConnections) {
              navigate('/dashboard');
            } else {
              navigate('/broker-connect');
            }
          } catch (redirErr) {
            console.warn('⚠️ Post-login connection check failed, defaulting to dashboard:', redirErr);
            navigate('/dashboard');
          }
        }
      });

      console.log('✅ Login process started - redirecting...');
    } catch (error) {
      console.error('Login error:', error);

      // MFA required — redirect to 2FA verify page
      // No need to setIsLoading(false) — component unmounts on navigation
      if (error.code === 'auth/multi-factor-auth-required') {
        navigate('/two-factor-verify');
        return;
      }

      setIsLoading(false);

      // Handle specific errors
      if (error.message === 'Username not found') {
        setValidationErrors(prev => ({
          ...prev,
          emailOrUsername: 'No account found with this username'
        }));
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setValidationErrors(prev => ({
          ...prev,
          password: 'Incorrect password'
        }));
      } else if (error.code === 'auth/too-many-requests') {
        setValidationErrors(prev => ({
          ...prev,
          password: 'Too many failed attempts. Please try again later'
        }));
      } else if (error.code === 'auth/user-disabled') {
        setValidationErrors(prev => ({
          ...prev,
          emailOrUsername: 'This account has been disabled'
        }));
      } else if (error.code === 'auth/user-not-found') {
        setValidationErrors(prev => ({
          ...prev,
          emailOrUsername: 'No account found with this email or username'
        }));
      }
    }
  };

  return (
    <>
      <HeadersNonAuth />
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 pt-32 pb-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Login</h1>
            <p className="text-gray-400"></p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-gray-900 rounded-lg p-8 space-y-6">
            {/* Email or Username */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Email or Username
              </label>
              <input
                type="text"
                name="emailOrUsername"
                value={formData.emailOrUsername}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                placeholder="your@email.com or username"
              />
              {validationErrors.emailOrUsername && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.emailOrUsername}</p>
              )}
            </div>
            
            {/* Password */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 pr-10 border border-gray-700 focus:border-purple-500 focus:outline-none"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
              {validationErrors.password && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
              )}
            </div>
            
            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  className="w-4 h-4 text-purple-500 bg-gray-800 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="ml-2 text-gray-300 text-sm">Remember me</span>
              </label>
              
              <Link
                to="/forgot-password"
                className="text-purple-500 hover:text-purple-400 text-sm"
              >
                Forgot password?
              </Link>
            </div>
            
            {/* Error Message */}
            {(error || validationErrors.form) && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-3 text-sm">
                {validationErrors.form || 'Login failed. Please check your credentials and try again.'}
              </div>
            )}
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || isChecking}
              className="w-full bg-purple-500 text-white rounded-lg py-3 font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing In...' : isChecking ? 'Checking...' : 'Sign In'}
            </button>
            
            {/* Self-registration is closed — no Sign Up link. */}
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage; 